import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { RefTypeItem } from '@sd-portal/shared/ref-type-item';
import { auditLogAdapter } from '../shared-audit.adapters';

import {
  AddressAudit,
  AircraftPerformanceAudit,
  AircraftProfileAudit,
  AircraftProfileEngineAudit,
  AircraftProfileComponentAudit,
  AircraftProfileSkeleton,
  AircraftRelationAudit,
  ColorSettingAudit,
  EmailAddressAudit,
  EngineAudit,
  ComponentAudit,
  PhoneNumberAudit,
  PostFlightVisibilityAudit,
  AircraftBillingRateAudit
} from '../../+models/aircraft-profile-audit.model';
import * as actions from './aircraft-profile.actions';

// States
interface AircraftProfileSkeletonState extends EntityState<AircraftProfileSkeleton> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface AircraftProfileAuditState extends EntityState<AircraftProfileAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AircraftPerformanceAuditState extends EntityState<AircraftPerformanceAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AircraftProfileEngineAuditState extends EntityState<AircraftProfileEngineAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface EngineAuditState extends EntityState<EngineAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}
interface AircraftProfileComponentAuditState extends EntityState<AircraftProfileComponentAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface ComponentAuditState extends EntityState<ComponentAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}
interface ColorSettingAuditState extends EntityState<ColorSettingAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface PostFlightVisibilityAuditState extends EntityState<PostFlightVisibilityAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AircraftBillingRateAuditState extends EntityState<AircraftBillingRateAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AircraftRelationAuditState extends EntityState<AircraftRelationAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface PhoneNumberAuditState extends EntityState<PhoneNumberAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface FaxNumberAuditState extends EntityState<PhoneNumberAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface EmailAddressAuditState extends EntityState<EmailAddressAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AddressAuditState extends EntityState<AddressAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface AircraftRelationTypeState extends EntityState<RefTypeItem> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loading: boolean;
  loaded: boolean;
}

interface AircraftRelationSubTypeState extends EntityState<RefTypeItem> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loading: boolean;
  loaded: boolean;
}

interface PostFlightVisibilityTypeState extends EntityState<RefTypeItem> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loading: boolean;
  loaded: boolean;
}

// Adapters
const aircraftProfileSkeletonAdapter = createEntityAdapter<AircraftProfileSkeleton>({
  selectId: p => p.aircraftProfileId
});
const aircraftRelationTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});
const aircraftRelationSubTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});
const postFlightVisibilityTypeAdapter = createEntityAdapter<RefTypeItem>({
  selectId: p => p.id
});

// entity selectors
export const aircraftProfileSkeletonSelectors = aircraftProfileSkeletonAdapter.getSelectors();
export const aircraftProfileAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftPerformanceAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftProfileEngineAuditSelectors = auditLogAdapter.getSelectors();
export const engineAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftProfileComponentAuditSelectors = auditLogAdapter.getSelectors();
export const componentAuditSelectors = auditLogAdapter.getSelectors();
export const colorSettingAuditSelectors = auditLogAdapter.getSelectors();
export const postFlightVisibilityAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftBillingRateAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftRelationAuditSelectors = auditLogAdapter.getSelectors();
export const phoneNumberAuditSelectors = auditLogAdapter.getSelectors();
export const faxNumberAuditSelectors = auditLogAdapter.getSelectors();
export const emailAddressAuditSelectors = auditLogAdapter.getSelectors();
export const addressAuditSelectors = auditLogAdapter.getSelectors();
export const aircraftRelationTypeSelectors = aircraftRelationTypeAdapter.getSelectors();
export const aircraftRelationSubTypeSelectors = aircraftRelationSubTypeAdapter.getSelectors();
export const postFlightVisibilityTypeSelectors = postFlightVisibilityTypeAdapter.getSelectors();

// Initial States
const aircraftProfileSkeletonInitialState = aircraftProfileSkeletonAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const aircraftProfileAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftPerformanceAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftProfileEngineAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const engineAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftProfileComponentAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const componentAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const colorSettingAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const postFlightVisibilityAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftBillingRateAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftRelationAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const phoneNumberAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const faxNumberAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const emailAddressAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const addressAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

const aircraftRelationTypeInitialState = aircraftRelationTypeAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const aircraftRelationSubTypeInitialState = aircraftRelationSubTypeAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const postFlightVisibilityTypeInitialState = postFlightVisibilityTypeAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

export interface State {
  aircraftProfileSkeleton: AircraftProfileSkeletonState;
  aircraftProfileAudit: AircraftProfileAuditState;
  aircraftPerformanceAudit: AircraftPerformanceAuditState;
  aircraftProfileEngineAudit: AircraftProfileEngineAuditState;
  engineAudit: EngineAuditState;
  aircraftProfileComponentAudit: AircraftProfileComponentAuditState;
  componentAudit: ComponentAuditState;
  colorSettingAudit: ColorSettingAuditState;
  postFlightVisibilityAudit: PostFlightVisibilityAuditState;
  aircraftBillingRateAudit: AircraftBillingRateAuditState;
  aircraftRelationAudit: AircraftRelationAuditState;
  phoneNumberAudit: PhoneNumberAuditState;
  faxNumberAudit: FaxNumberAuditState;
  emailAddressAudit: EmailAddressAuditState;
  addressAudit: AddressAuditState;
  aircraftRelationType: AircraftRelationTypeState;
  aircraftRelationSubType: AircraftRelationSubTypeState;
  postFlightVisibilityType: PostFlightVisibilityTypeState;
}

const initialState: State = {
  aircraftProfileSkeleton: aircraftProfileSkeletonInitialState,
  aircraftProfileAudit: aircraftProfileAuditInitialState,
  aircraftPerformanceAudit: aircraftPerformanceAuditInitialState,
  aircraftProfileEngineAudit: aircraftProfileEngineAuditInitialState,
  engineAudit: engineAuditInitialState,
  aircraftProfileComponentAudit: aircraftProfileComponentAuditInitialState,
  componentAudit: componentAuditInitialState,
  colorSettingAudit: colorSettingAuditInitialState,
  postFlightVisibilityAudit: postFlightVisibilityAuditInitialState,
  aircraftBillingRateAudit: aircraftBillingRateAuditInitialState,
  aircraftRelationAudit: aircraftRelationAuditInitialState,
  phoneNumberAudit: phoneNumberAuditInitialState,
  faxNumberAudit: faxNumberAuditInitialState,
  emailAddressAudit: emailAddressAuditInitialState,
  addressAudit: addressAuditInitialState,
  aircraftRelationType: aircraftRelationTypeInitialState,
  aircraftRelationSubType: aircraftRelationSubTypeInitialState,
  postFlightVisibilityType: postFlightVisibilityTypeInitialState
};

const aircraftProfileReducer = createReducer<State>(
  initialState,

  on(actions.ResetAircraftProfile, () => initialState),

  on(actions.LoadAircraftProfileAuditSkeleton, state => ({
    ...state,

    aircraftProfileSkeleton: {
      ...state.aircraftProfileSkeleton,
      loading: true
    }
  })),

  on(actions.AircraftProfileAuditSkeletonLoaded, (state, action) => ({
    ...state,

    aircraftProfileSkeleton: {
      ...state.aircraftProfileSkeleton,
      ...aircraftProfileSkeletonAdapter.addOne(action, state.aircraftProfileSkeleton),
      loaded: true,
      loading: false
    }
  })),

  on(actions.LoadAircraftRelationTypes, state => ({
    ...state,

    aircraftRelationType: {
      ...state.aircraftRelationType,
      loading: true
    }
  })),

  on(actions.AircraftRelationTypesLoaded, (state, action) => ({
    ...state,

    aircraftRelationType: {
      ...state.aircraftRelationType,
      ...aircraftRelationTypeAdapter.setAll(action.payload, state.aircraftRelationType),
      loaded: true,
      loading: false
    }
  })),

  on(actions.LoadAircraftRelationSubTypes, state => ({
    ...state,

    aircraftRelationSubType: {
      ...state.aircraftRelationSubType,
      loading: true
    }
  })),

  on(actions.AircraftRelationSubTypeLoaded, (state, action) => ({
    ...state,

    aircraftRelationSubType: {
      ...state.aircraftRelationSubType,
      ...aircraftRelationSubTypeAdapter.setAll(action.payload, state.aircraftRelationSubType),
      loaded: true,
      loading: false
    }
  })),

  on(actions.LoadPostFlightVisibilityTypes, state => ({
    ...state,

    postFlightVisibilityType: {
      ...state.postFlightVisibilityType,
      loading: true
    }
  })),

  on(actions.PostFlightVisibilityTypesLoaded, (state, action) => ({
    ...state,

    postFlightVisibilityType: {
      ...state.postFlightVisibilityType,
      ...postFlightVisibilityTypeAdapter.setAll(action.payload, state.postFlightVisibilityType),
      loaded: true,
      loading: false
    }
  })),

  on(actions.LoadAircraftProfileAudit, (state, action) => ({
    ...state,

    aircraftProfileAudit: {
      ...state.aircraftProfileAudit,
      lastAuditLogId:
        state.aircraftProfileAudit.selectedId === action.aircraftProfileId
          ? state.aircraftProfileAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftProfileAuditLoaded, (state, action) => ({
    ...state,

    aircraftProfileAudit: {
      ...state.aircraftProfileAudit,
      ...(!state.aircraftProfileAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftProfileAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftProfileAudit)),
      loaded: true,
      loading: false,
      selectedId: action.aircraftProfileId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadColorSettingAudit, (state, action) => ({
    ...state,

    colorSettingAudit: {
      ...state.colorSettingAudit,
      lastAuditLogId:
        state.colorSettingAudit.selectedId === action.colorSettingId
          ? state.colorSettingAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.ColorSettingAuditLoaded, (state, action) => ({
    ...state,

    colorSettingAudit: {
      ...state.colorSettingAudit,
      ...(!state.colorSettingAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.colorSettingAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.colorSettingAudit)),
      loaded: true,
      loading: false,
      selectedId: action.colorSettingId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAircraftPerformanceAudit, (state, action) => ({
    ...state,

    aircraftPerformanceAudit: {
      ...state.aircraftPerformanceAudit,
      lastAuditLogId:
        state.aircraftPerformanceAudit.selectedId === action.aircraftPerformanceId
          ? state.aircraftPerformanceAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftPerformanceAuditLoaded, (state, action) => ({
    ...state,

    aircraftPerformanceAudit: {
      ...state.aircraftPerformanceAudit,
      ...(!state.aircraftPerformanceAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftPerformanceAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftPerformanceAudit)),
      loaded: true,
      loading: false,
      selectedId: action.aircraftPerformanceId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAircraftProfileEngineAudit, (state, action) => ({
    ...state,

    aircraftProfileEngineAudit: {
      ...state.aircraftProfileEngineAudit,
      lastAuditLogId:
        state.aircraftProfileEngineAudit.selectedId === action.aircraftProfileEngineId
          ? state.aircraftProfileEngineAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftProfileEngineAuditLoaded, (state, action) => ({
    ...state,

    aircraftProfileEngineAudit: {
      ...state.aircraftProfileEngineAudit,
      ...(!state.aircraftProfileEngineAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftProfileEngineAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftProfileEngineAudit)),
      loaded: true,
      loading: false,
      selectedId: action.aircraftProfileEngineId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadEngineAudit, (state, action) => ({
    ...state,

    engineAudit: {
      ...state.engineAudit,
      lastAuditLogId:
        state.engineAudit.selectedId === action.engineId ? state.engineAudit.lastAuditLogId : null,
      loading: true
    }
  })),

  on(actions.EngineAuditLoaded, (state, action) => ({
    ...state,

    engineAudit: {
      ...state.engineAudit,
      ...(!state.engineAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.engineAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.engineAudit)),
      loaded: true,
      loading: false,
      selectedId: action.engineId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAircraftProfileComponentAudit, (state, action) => ({
    ...state,

    aircraftProfileComponentAudit: {
      ...state.aircraftProfileComponentAudit,
      lastAuditLogId:
        state.aircraftProfileComponentAudit.selectedId === action.aircraftProfileComponentId
          ? state.aircraftProfileComponentAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftProfileComponentAuditLoaded, (state, action) => ({
    ...state,

    aircraftProfileComponentAudit: {
      ...state.aircraftProfileComponentAudit,
      ...(!state.aircraftProfileComponentAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftProfileComponentAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftProfileComponentAudit)),
      loaded: true,
      loading: false,
      selectedId: action.aircraftProfileComponentId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadComponentAudit, (state, action) => ({
    ...state,

    componentAudit: {
      ...state.componentAudit,
      lastAuditLogId:
        state.componentAudit.selectedId === action.componentId
          ? state.componentAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.ComponentAuditLoaded, (state, action) => ({
    ...state,

    componentAudit: {
      ...state.componentAudit,
      ...(!state.componentAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.componentAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.componentAudit)),
      loaded: true,
      loading: false,
      selectedId: action.componentId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAircraftRelationAudit, (state, action) => ({
    ...state,

    aircraftRelationAudit: {
      ...state.aircraftRelationAudit,
      lastAuditLogId:
        state.aircraftRelationAudit.selectedId === action.aircraftRelationId
          ? state.aircraftRelationAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftRelationAuditLoaded, (state, action) => ({
    ...state,

    aircraftRelationAudit: {
      ...state.aircraftRelationAudit,
      ...(!state.aircraftRelationAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftRelationAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftRelationAudit)),
      loaded: true,
      loading: false,
      selectedId: action.aircraftRelationId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadPhoneNumberAudit, (state, action) => ({
    ...state,

    phoneNumberAudit: {
      ...state.phoneNumberAudit,
      lastAuditLogId:
        state.phoneNumberAudit.selectedId === action.phoneNumberId
          ? state.phoneNumberAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PhoneNumberAuditLoaded, (state, action) => ({
    ...state,

    phoneNumberAudit: {
      ...state.phoneNumberAudit,
      ...(!state.phoneNumberAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.phoneNumberAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.phoneNumberAudit)),
      loaded: true,
      loading: false,
      selectedId: action.phoneNumberId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadFaxNumberAudit, (state, action) => ({
    ...state,

    faxNumberAudit: {
      ...state.faxNumberAudit,
      lastAuditLogId:
        state.faxNumberAudit.selectedId === action.phoneNumberId
          ? state.faxNumberAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.FaxNumberAuditLoaded, (state, action) => ({
    ...state,

    faxNumberAudit: {
      ...state.faxNumberAudit,
      ...(!state.faxNumberAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.colorSettingAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.colorSettingAudit)),
      loaded: true,
      loading: false,
      selectedId: action.phoneNumberId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadEmailAddressAudit, (state, action) => ({
    ...state,

    emailAddressAudit: {
      ...state.emailAddressAudit,
      lastAuditLogId:
        state.emailAddressAudit.selectedId === action.emailAddressId
          ? state.emailAddressAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.EmailAddressAuditLoaded, (state, action) => ({
    ...state,

    emailAddressAudit: {
      ...state.emailAddressAudit,
      ...(!state.emailAddressAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.emailAddressAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.emailAddressAudit)),
      loaded: true,
      loading: false,
      selectedId: action.emailAddressId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAddressAudit, (state, action) => ({
    ...state,

    addressAudit: {
      ...state.addressAudit,
      lastAuditLogId:
        state.addressAudit.selectedId === action.addressId
          ? state.addressAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AddressAuditLoaded, (state, action) => ({
    ...state,

    addressAudit: {
      ...state.addressAudit,
      ...(!state.addressAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.addressAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.addressAudit)),
      loaded: true,
      loading: false,
      selectedId: action.addressId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadPostFlightVisibilityAudit, (state, action) => ({
    ...state,

    postFlightVisibilityAudit: {
      ...state.postFlightVisibilityAudit,
      lastAuditLogId:
        state.postFlightVisibilityAudit.selectedId === action.postFlightVisibilityId
          ? state.postFlightVisibilityAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.PostFlightVisibilityAuditLoaded, (state, action) => ({
    ...state,

    postFlightVisibilityAudit: {
      ...state.postFlightVisibilityAudit,
      ...(!state.postFlightVisibilityAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.postFlightVisibilityAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.postFlightVisibilityAudit)),
      loaded: true,
      loading: false,
      selectedId: action.postFlightVisibilityId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadAircraftBillingRateAudit, (state, action) => ({
    ...state,

    aircraftBillingRateAudit: {
      ...state.aircraftBillingRateAudit,
      lastAuditLogId:
        state.aircraftBillingRateAudit.selectedId === action.aircraftBillingRateId
          ? state.aircraftBillingRateAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.AircraftBillingRateAuditLoaded, (state, action) => ({
    ...state,

    aircraftBillingRateAudit: {
      ...state.aircraftBillingRateAudit,
      ...(!state.aircraftBillingRateAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.aircraftBillingRateAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.aircraftBillingRateAudit)),
      loaded: true,
      loading: false,
      selectedId: action.aircraftBillingRateId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return aircraftProfileReducer(state, action);
}
